import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Divider,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginValidationSchema } from '../utils/validationSchemas';
import { hasFieldError, getFieldHelperText } from '../hooks/useFormValidation';
import { useAuth } from '../contexts/AuthContext';
import ConstantComplyLogo from '../assets/logos/ConstantComplyLogo';

interface LoginFormData {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    watch,
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginValidationSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  });

  const emailValue = watch('email');
  const passwordValue = watch('password');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  // Clear a previous login error only after the person edits the form.
  // Demo login does not change these fields, so its message must stay visible.
  useEffect(() => {
    setError('');
  }, [emailValue, passwordValue]);

  const onFormSubmit = async (data: LoginFormData) => {
    setError('');
    setIsLoading(true);

    try {
      const result = await login(data.email, data.password);
      
      if (result.success) {
        const from = (location.state as any)?.from?.pathname || '/';
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = async (role: 'admin' | 'manager' | 'employee') => {
    setError('');
    setIsLoading(true);

    // Demo credentials (you can adjust these based on your test data)
    const demoCredentials = {
      admin: { email: 'admin@test.com', password: 'admin123' },
      manager: { email: 'manager@test.com', password: 'manager123' },
      employee: { email: 'employee@test.com', password: 'employee123' },
    };

    const { email: demoEmail, password: demoPassword } = demoCredentials[role];

    try {
      const result = await login(demoEmail, demoPassword);
      
      if (result.success) {
        const from = (location.state as any)?.from?.pathname || '/';
        navigate(from, { replace: true });
      } else {
        setError(`Demo ${role} account is not available. Sign in with your own email and password.`);
      }
    } catch (err) {
      setError(`Demo ${role} account is not available. Sign in with your own email and password.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth={false}>
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: '80%', md: '40%', lg: '33%' }, maxWidth: 560 }}>
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
          <Box sx={{ mb: 1, textAlign: 'center' }}>
            <ConstantComplyLogo width={200} height={110} color="#1976d2" />
            <Typography
              variant="h5"
              component="div"
              sx={{
                mt: 1,
                color: '#1976d2',
                fontWeight: 'bold',
                letterSpacing: '-0.02em',
                fontFamily: 'Arial, sans-serif',
              }}
            >
              ConstantComply
            </Typography>
          </Box>
          
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Sign in to your account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onFormSubmit)} sx={{ mt: 1, width: '100%' }}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  autoComplete="email"
                  autoFocus
                  error={hasFieldError(errors, 'email')}
                  helperText={getFieldHelperText(errors, 'email')}
                  disabled={isLoading || isSubmitting}
                />
              )}
            />
            
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  required
                  fullWidth
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  error={hasFieldError(errors, 'password')}
                  helperText={getFieldHelperText(errors, 'password')}
                  disabled={isLoading || isSubmitting}
                />
              )}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading || isSubmitting || !isValid}
            >
              {isLoading || isSubmitting ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary">
                  Forgot your password?
                </Typography>
              </Link>
            </Box>

            {process.env.NODE_ENV !== 'production' && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Demo Accounts
                  </Typography>
                </Divider>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleDemoLogin('admin')}
                    disabled={isLoading}
                  >
                    Demo Admin Login
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleDemoLogin('manager')}
                    disabled={isLoading}
                  >
                    Demo Manager Login
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleDemoLogin('employee')}
                    disabled={isLoading}
                  >
                    Demo Employee Login
                  </Button>
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account? Contact your administrator for an invitation.
            </Typography>
          </Box>
        </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default Login;